import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './LanguageSwitcher.module.css';

const languages = [
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'zh-CN', name: '中文', short: '中文' },
  { code: 'ja', name: '日本語', short: 'JA' },
  { code: 'ko', name: '한국어', short: 'KO' },
  { code: 'vi', name: 'Tiếng Việt', short: 'VI' },
  { code: 'th', name: 'ไทย', short: 'TH' },
];

interface LanguageSwitcherProps {
  variant?: 'nav' | 'booking';
}

const TranslateIcon: React.FC = () => (
  <img src="/assets/translate.svg" alt="Translate" width={20} height={20} />
);

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'nav' }) => {
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('preferred_language') || 'en';
  });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const triggerGoogleTranslate = useCallback((langCode: string) => {
    const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
    if (selectElement) {
      selectElement.value = langCode;
      selectElement.dispatchEvent(new Event('change'));
    }
  }, []);

  const changeLanguage = (langCode: string) => {
    setCurrentLang(langCode);
    setLangMenuOpen(false);
    localStorage.setItem('preferred_language', langCode);
    triggerGoogleTranslate(langCode);
  };

  // Restore saved language on mount
  useEffect(() => {
    const saved = localStorage.getItem('preferred_language');
    if (saved && saved !== 'en') {
      let attempts = 0;
      const interval = setInterval(() => {
        const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (selectElement) {
          clearInterval(interval);
          triggerGoogleTranslate(saved);
        }
        attempts++;
        if (attempts > 30) {
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [triggerGoogleTranslate]);

  // Guard against Google Translate modifying body.style.top and body.style.position
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const top = document.body.style.top;
      if (top && top !== '0px' && top !== '') {
        document.body.style.top = '0px';
      }
      if (document.body.style.position === 'relative') {
        document.body.style.position = '';
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dropdownClass = variant === 'nav' ? styles.dropdownNav : styles.dropdownBooking;
  const isTranslating = currentLang !== 'en';
  const currentLangData = languages.find(l => l.code === currentLang);

  return (
    <div className={styles.wrapper} ref={wrapperRef} translate="no">
      <button
        className={`${styles.button} ${isTranslating ? styles.buttonTranslating : ''} ${langMenuOpen ? styles.buttonActive : ''}`}
        onClick={() => setLangMenuOpen(!langMenuOpen)}
        aria-label="Change language"
      >
        <TranslateIcon />
        {isTranslating && currentLangData && (
          <>
            <span className={styles.langSeparator}>:</span>
            <span className={styles.langLabel}>{currentLangData.short}</span>
          </>
        )}
      </button>
      {langMenuOpen && (
        <div className={`${styles.dropdown} ${dropdownClass}`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`${styles.langOption} ${currentLang === lang.code ? styles.langOptionActive : ''}`}
            >
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
